/*
 * Application State should be all within this file.
 */

const EMPTY_DESIGN = () => ({
  slotAssignments: {},
  simResult: null,
  validationResult: null,
  designSubmitted: false,
  requirementsConfirmed: false,
  requirementsAnswers: null
});

const app = {
  state: {
    selectedScenarioId: 1,
    scenarioDesigns: {},
    activeSlot: null,
    fittedCurve: null,
    preLabRows: [],
    preLabComplete: false
  },
  mutations: {
    SET_SCENARIO(state, id) {
      state.selectedScenarioId = id;
      state.activeSlot = null;
    },
    ASSIGN_SLOT(state, { slotId, component }) {
      const id = state.selectedScenarioId;
      const design = state.scenarioDesigns[id] || EMPTY_DESIGN();
      state.scenarioDesigns = {
        ...state.scenarioDesigns,
        [id]: {
          ...design,
          slotAssignments: { ...design.slotAssignments, [slotId]: component }
        }
      };
    },
    CLEAR_SLOT(state, slotId) {
      const id = state.selectedScenarioId;
      const design = state.scenarioDesigns[id];
      if (!design) return;
      const updated = { ...design.slotAssignments };
      delete updated[slotId];
      state.scenarioDesigns = {
        ...state.scenarioDesigns,
        [id]: { ...design, slotAssignments: updated }
      };
    },
    SET_ACTIVE_SLOT(state, slotId) {
      state.activeSlot = slotId;
    },
    CLEAR_ALL_SLOTS(state) {
      const id = state.selectedScenarioId;
      state.scenarioDesigns = {
        ...state.scenarioDesigns,
        [id]: EMPTY_DESIGN()
      };
      state.activeSlot = null;
    },
    SET_SCENARIO_DESIGNS(state, designs) {
      state.scenarioDesigns = designs || {};
    },
    SET_SUBMISSION(state, { simResult, validationResult }) {
      const id = state.selectedScenarioId;
      const design = state.scenarioDesigns[id] || EMPTY_DESIGN();
      state.scenarioDesigns = {
        ...state.scenarioDesigns,
        [id]: {
          ...design,
          simResult,
          validationResult,
          designSubmitted: true
        }
      };
    },
    SET_REQUIREMENTS_CONFIRMED(state, val) {
      const id = state.selectedScenarioId;
      const design = state.scenarioDesigns[id] || EMPTY_DESIGN();
      state.scenarioDesigns = {
        ...state.scenarioDesigns,
        [id]: { ...design, requirementsConfirmed: val }
      };
    },
    SET_REQUIREMENTS_ANSWERS(state, answers) {
      const id = state.selectedScenarioId;
      const design = state.scenarioDesigns[id] || EMPTY_DESIGN();
      state.scenarioDesigns = {
        ...state.scenarioDesigns,
        [id]: { ...design, requirementsAnswers: answers }
      };
    },
    SET_FITTED_CURVE(state, curve) {
      state.fittedCurve = curve;
    },
    SET_PRELAB_ROWS(state, rows) {
      state.preLabRows = rows;
    },
    SET_PRELAB_COMPLETE(state, val) {
      state.preLabComplete = val;
    }
  },
  actions: {
    setScenario({ commit }, id) {
      commit("SET_SCENARIO", id);
    },
    assignSlot({ commit }, { slotId, component }) {
      commit("ASSIGN_SLOT", { slotId, component });
    },
    clearSlot({ commit }, slotId) {
      commit("CLEAR_SLOT", slotId);
    },
    setActiveSlot({ commit }, slotId) {
      commit("SET_ACTIVE_SLOT", slotId);
    },
    clearDesign({ commit }) {
      commit("CLEAR_ALL_SLOTS");
    },
    submitDesignResult({ commit }, { simResult, validationResult }) {
      commit("SET_SUBMISSION", { simResult, validationResult });
    },
    setRequirementsConfirmed({ commit }, val) {
      commit("SET_REQUIREMENTS_CONFIRMED", val);
    },
    setRequirementsAnswers({ commit }, answers) {
      commit("SET_REQUIREMENTS_ANSWERS", answers);
    },
    setFittedCurve({ commit }, curve) {
      commit("SET_FITTED_CURVE", curve);
    },
    setPreLabRows({ commit }, rows) {
      commit("SET_PRELAB_ROWS", rows);
    },
    completePreLab({ commit }) {
      commit("SET_PRELAB_COMPLETE", true);
    },
    mapFromDatastore({ commit, state }, payload) {
      let _payload = "";
      try {
        _payload = JSON.parse(payload);
      } catch (e) {
        _payload = payload;
      }

      commit(
        "SET_SCENARIO",
        _payload?.selectedScenarioId ?? state.selectedScenarioId
      );

      // Handle legacy flat slotAssignments → migrate into scenarioDesigns
      if (_payload?.scenarioDesigns) {
        commit("SET_SCENARIO_DESIGNS", _payload.scenarioDesigns);
      } else if (_payload?.slotAssignments) {
        const id = _payload.selectedScenarioId ?? state.selectedScenarioId;
        commit("SET_SCENARIO_DESIGNS", {
          [id]: {
            slotAssignments: _payload.slotAssignments,
            simResult: null,
            validationResult: null,
            designSubmitted: false
          }
        });
      }

      commit("SET_FITTED_CURVE", _payload?.fittedCurve ?? state.fittedCurve);
      commit("SET_PRELAB_ROWS", _payload?.preLabRows ?? state.preLabRows);
      commit(
        "SET_PRELAB_COMPLETE",
        _payload?.preLabComplete ?? state.preLabComplete
      );
    }
  },
  getters: {
    selectedScenarioId(state) {
      return state.selectedScenarioId;
    },
    slotAssignments(state) {
      const design = state.scenarioDesigns[state.selectedScenarioId];
      return design ? design.slotAssignments : {};
    },
    activeSlot(state) {
      return state.activeSlot;
    },
    placedComponents(state, getters) {
      const assignments = getters.slotAssignments;
      return Object.entries(assignments)
        .filter(([, comp]) => comp !== null && comp !== undefined)
        .map(([slotId, comp]) => ({ ...comp, slotId }));
    },
    totalCost(state, getters) {
      return Object.values(getters.slotAssignments)
        .filter((c) => c !== null && c !== undefined)
        .reduce((sum, c) => sum + (c.price || 0), 0);
    },
    filledSlotCount(state, getters) {
      return Object.values(getters.slotAssignments).filter(
        (c) => c !== null && c !== undefined
      ).length;
    },
    simResult(state) {
      const design = state.scenarioDesigns[state.selectedScenarioId];
      return design ? design.simResult : null;
    },
    validationResult(state) {
      const design = state.scenarioDesigns[state.selectedScenarioId];
      return design ? design.validationResult : null;
    },
    designSubmitted(state) {
      const design = state.scenarioDesigns[state.selectedScenarioId];
      return design ? design.designSubmitted : false;
    },
    requirementsConfirmed(state) {
      const design = state.scenarioDesigns[state.selectedScenarioId];
      return design?.requirementsConfirmed ?? false;
    },
    requirementsAnswers(state) {
      const design = state.scenarioDesigns[state.selectedScenarioId];
      return design?.requirementsAnswers ?? null;
    },
    fittedCurve(state) {
      return state.fittedCurve;
    },
    isPreLabComplete(state) {
      return state.preLabComplete;
    },
    preLabRows(state) {
      return state.preLabRows;
    },
    mapToDatastore(state) {
      let payload = {
        selectedScenarioId: state.selectedScenarioId,
        scenarioDesigns: state.scenarioDesigns,
        fittedCurve: state.fittedCurve,
        preLabRows: state.preLabRows,
        preLabComplete: state.preLabComplete
      };

      payload = JSON.stringify(payload);
      return payload;
    }
  }
};

export default app;
